import { formatCurrency } from "@/lib/hooks/utils";
import { SaleListResponseDto } from "@/lib/interfaces/sales";
import { DataTableProps } from "@/lib/interfaces/utils";
import { getStatusText } from "@/lib/utils/sale-status";
import { Button } from "@/ui-components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/ui-components/ui/table";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { format } from "date-fns";
import { Clock, FileText, User } from "lucide-react";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

export const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case "open":
      return "bg-blue-50 text-blue-700 ring-blue-700/10";
    case "draft":
      return "bg-gray-50 text-gray-700 ring-gray-700/10";
    case "completed":
      return "bg-green-50 text-green-700 ring-green-700/10";
    case "cancelled":
      return "bg-red-50 text-red-700 ring-red-700/10";
    case "closed":
      return "bg-gray-50 text-gray-700 ring-gray-700/10";
    default:
      return "bg-blue-50 text-blue-700 ring-blue-700/10";
  }
};

const SalesDataTable = ({
  data,
  isLoading,
  pageCount,
  pagination,
  setPagination,
  setReload,
}: DataTableProps<SaleListResponseDto>) => {
  const [rowSelection, setRowSelection] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const columns: ColumnDef<SaleListResponseDto>[] = [
    {
      accessorKey: "title",
      header: () => {
        return (
          <div className="flex items-center">
            <p>Titel</p>
          </div>
        );
      },
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            <span className="font-medium">{row.original.title}</span>
          </div>
        );
      },
    },
    {
      accessorKey: "person",
      header: () => {
        return (
          <div className="flex items-center">
            <p>Person</p>
          </div>
        );
      },
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-2">
            <User className="h-4 w-4" />
            <span>{row.original.belongsToUserFullName}</span>
          </div>
        );
      },
    },
    {
      accessorKey: "status",
      header: () => {
        return (
          <div className="flex items-center">
            <p>Status</p>
          </div>
        );
      },
      cell: ({ row }) => {
        return (
          <div className="flex">
            <span
              className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${getStatusColor(
                row.original.state
              )}`}
            >
              {getStatusText(row.original.state)}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "totalNetPrice",
      header: () => {
        return (
          <div className="flex items-center">
            <p>Gesamtpreis Netto</p>
          </div>
        );
      },
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-2">
            <span>{formatCurrency(row.original.totalNetPrice)}</span>
          </div>
        );
      },
    },
    {
      accessorKey: "totalGrossPrice",
      header: () => {
        return (
          <div className="flex items-center">
            <p>Gesamtpreis Brutto</p>
          </div>
        );
      },
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-2">
            <span>{formatCurrency(row.original.totalGrossPrice)}</span>
          </div>
        );
      },
    },
    {
      accessorKey: "createdAt",
      header: () => {
        return (
          <div className="flex items-center">
            <p>Erstellt am</p>
          </div>
        );
      },
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            <span>{format(new Date(row.original.createdAt), "dd.MM.yyyy HH:mm")}</span>
          </div>
        );
      },
    },
  ];

  const table = useReactTable({
    data,
    columns,
    pageCount,
    manualPagination: true,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: setRowSelection,
    initialState: { pagination: { pageSize: 20 } },
    state: {
      pagination,
      rowSelection,
    },
  });

  if (isLoading) {
    return <div>Lädt...</div>;
  }

  return (
    <div className="w-full">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className="cursor-pointer hover:bg-gray-100"
                  onClick={() => navigate(`/sales/${row.original._id}`)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  Keine Verkäufe gefunden.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between space-x-2 py-4">
        <Button
          className="text-[17px] text-[#888] hover:text-[#12282A]"
          variant="outline"
          size="sm"
          onClick={() => {
            table.previousPage();
            const params = new URLSearchParams(searchParams);
            params.set("page", table.getState().pagination.pageIndex.toString());
            setSearchParams(params);
          }}
          disabled={!table.getCanPreviousPage()}
        >
          Letzte
        </Button>
        <span className="text-sm text-[#D0D5DD]">
          Seite {table.getState().pagination.pageIndex + 1} von {table.getPageCount()}
        </span>
        <Button
          className="text-[17px] text-[#888] hover:text-[#12282A]"
          variant="outline"
          size="sm"
          onClick={() => {
            table.nextPage();
            const params = new URLSearchParams(searchParams);
            params.set("page", (table.getState().pagination.pageIndex + 2).toString());
            setSearchParams(params);
          }}
          disabled={!table.getCanNextPage()}
        >
          Nächste
        </Button>
      </div>
    </div>
  );
};

export default SalesDataTable;
